import "./knob.css";
import { compactSize } from "./responsive.ts";

// Knob — the signature control of the synth.
//
// Visual structure (outside-in):
//   .knob (DOM)             — outer wrapper carrying L2 box-shadow (raised look)
//     .knob-halo (DOM)       — absolutely-positioned glow ring (ice-blue), opacity-animated
//     <canvas>               — multi-pass dial face: base, recessed track, active arc, indicator
//     .knob-readout (DOM)   — numerical value, fades in during interaction
//   .knob-label (DOM)        — small lowercase label below, optional
//
// The CSS does what CSS does well (soft outer shadow, halo, fade animations);
// the canvas does what canvas does well (the dial geometry with pixel-exact
// arcs and a luminous indicator). Both layers respect devicePixelRatio.

const TAU = Math.PI * 2;

// Classic synth-knob arc: 7:30 clock (bottom-left) → up through 12 → down to
// 4:30 (bottom-right), sweeping 270° clockwise.
//
// In canvas coordinates (y down, angles measured from +x clockwise):
//   3 o'clock = 0    6 o'clock = π/2   9 o'clock = π   12 o'clock = 3π/2
// so 7:30 → 3π/4, and 4:30 → π/4. To sweep clockwise from 7:30 through 12
// to 4:30 we walk the arc from 3π/4 to (π/4 + 2π) = 9π/4.
const START_ANGLE = (3 * Math.PI) / 4;
const END_ANGLE = Math.PI / 4 + TAU;
const ARC_SWEEP = END_ANGLE - START_ANGLE; // 3π/2 = 270°

/** Visual damping time constant. Knob position lerps toward the target value
 * at this τ in ms, giving the rotation a faint physical inertia. */
const VISUAL_TAU_MS = 35;

/** Glow intensity time constant. The single-channel "glowAmount" lerps
 * toward its target (0 idle, 0.55 hover, 1 dragging) at this τ. Slower
 * than the value damping so the brightening feels like a swell, not a
 * twitch. */
const GLOW_TAU_MS = 200;

/** Pixels of vertical drag for full-range traversal at default sensitivity. */
const FULL_RANGE_PX = 220;

/** Extra canvas padding around the visual knob, in CSS pixels. Lets glow
 * shadows bleed outside the knob's bounding box without getting clipped at
 * the canvas edge. Sized to comfortably exceed the largest shadow blur
 * radius used by either knob style at peak interaction. */
const GLOW_PADDING = 28;

/** Ice-blue colour stops used to interpolate the indicator colour as
 * `glowAmount` rises. RGB triplets; matches `#7dd3fc` and `#bae6fd`. */
const COLOR_DIM = [125, 211, 252] as const;
const COLOR_BRIGHT = [186, 230, 253] as const;
/** Saturated ice blue used for the ice-mode shadow tint (`#38bdf8`). */
const SHADOW_ICE = [56, 189, 248] as const;

/** Volume-mode colour stops. Linear interpolation green → amber → red. */
const VOL_GREEN = [74, 222, 128] as const; // #4ade80
const VOL_AMBER = [252, 211, 77] as const; // #fcd34d
const VOL_RED = [239, 68, 68] as const; // #ef4444
/** Position of the amber stop in the volume ramp (0..1). Below this the
 * ramp interpolates green → amber; above, amber → red. */
const VOL_AMBER_STOP = 0.7;

type Rgb = readonly [number, number, number];

function lerpIce(t: number): Rgb {
  return [
    Math.round(COLOR_DIM[0] + (COLOR_BRIGHT[0] - COLOR_DIM[0]) * t),
    Math.round(COLOR_DIM[1] + (COLOR_BRIGHT[1] - COLOR_DIM[1]) * t),
    Math.round(COLOR_DIM[2] + (COLOR_BRIGHT[2] - COLOR_DIM[2]) * t),
  ];
}

/** Volume-mode colour at normalized position `t` (0..1). */
function volumeColor(t: number): Rgb {
  const clamped = Math.max(0, Math.min(1, t));
  let lo: Rgb;
  let hi: Rgb;
  let local: number;
  if (clamped < VOL_AMBER_STOP) {
    lo = VOL_GREEN;
    hi = VOL_AMBER;
    local = clamped / VOL_AMBER_STOP;
  } else {
    lo = VOL_AMBER;
    hi = VOL_RED;
    local = (clamped - VOL_AMBER_STOP) / (1 - VOL_AMBER_STOP);
  }
  return [
    Math.round(lo[0] + (hi[0] - lo[0]) * local),
    Math.round(lo[1] + (hi[1] - lo[1]) * local),
    Math.round(lo[2] + (hi[2] - lo[2]) * local),
  ];
}

function rgbStr(c: Rgb): string {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

function rgbaStr(c: Rgb, alpha: number): string {
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})`;
}

export type KnobTaper = "linear" | "log";

/** Visual style of the knob.
 *   - `"arc"` (default) — cap with an ice-blue arc + indicator notch sweeping
 *     7:30 → 4:30 through the top.
 *   - `"led"` — smaller raised cap surrounded by a ring of discrete LED dots
 *     that light up to indicate value, with a single bright dot on the cap
 *     face for fine position. Modelled on the reference image. */
export type KnobStyle = "arc" | "led";

/** Colour scheme for the indicator.
 *   - `"ice"` (default) — ice-blue family; brightness lerps with glow.
 *   - `"volume"` — green→amber→red ramp driven by the knob's value. For VU
 *     and master-volume controls. In LED style, each lit dot picks up its
 *     own colour from the ramp at its position (classic VU meter look);
 *     in arc style, the whole arc and notch take the current value's
 *     colour. */
export type KnobColorMode = "ice" | "volume";

/** State the demo page can pin a knob into for the static showcase. */
export type ForcedState = "idle" | "hover" | "active" | "disabled";

export interface KnobOptions {
  container: HTMLElement;
  size?: number;
  min: number;
  max: number;
  default: number;
  value?: number;
  taper?: KnobTaper;
  style?: KnobStyle;
  /** Colour scheme for the indicator. Default `"ice"`. */
  colorMode?: KnobColorMode;
  /** LED ring tick count (only used when `style === "led"`). Default 17. */
  ledCount?: number;
  /** Snap values to multiples of `step`. Use 1 for integer-valued knobs
   *  (octave, semitone offsets) so they don't drift between integers
   *  while dragging. Default 0 (no snapping). */
  step?: number;
  label?: string;
  /** Format the numerical readout (default: 2 decimal places). */
  format?: (value: number) => string;
  onChange?: (value: number) => void;
  /** Force a visual state for the demo page. Disables interaction. */
  forceState?: ForcedState;
}

export class Knob {
  private root: HTMLDivElement;
  private halo: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private readout: HTMLOutputElement;
  private labelEl: HTMLSpanElement | null = null;

  private size: number;
  private min: number;
  private max: number;
  private defaultValue: number;
  private taper: KnobTaper;
  private style: KnobStyle;
  private colorMode: KnobColorMode;
  private ledCount: number;
  private step: number;
  private format: (v: number) => string;
  private onChange: ((value: number) => void) | undefined;

  /** Target value the user has set. */
  private value: number;
  /** Rendered value, lerping toward `value` for the visual inertia. */
  private visualValue: number;

  /** Glow intensity (0..1). Lerps toward `glowTarget()` each frame; drives
   * shadow blur and indicator colour on the canvas. */
  private glowAmount = 0;

  /** Signal level (0..1) flowing through this control. Only used when
   * `colorMode === "volume"`: the indicator colour is sampled from the
   * green → amber → red ramp at this position, *independent of the knob's
   * value*. The knob value still determines how much of the arc is lit
   * (or how many LED dots are on); the signal level only sets the colour.
   * Update via [`setSignalLevel`](Self::setSignalLevel). */
  private signalLevel = 0;

  /** Drag state. */
  private dragging = false;
  private hovering = false;
  private dragStartY = 0;
  private dragStartValue = 0;

  private forced: ForcedState | undefined;

  private rafHandle = 0;
  private readoutFadeTimer: number | undefined;
  private lastRenderTime = 0;

  constructor(opts: KnobOptions) {
    // Apply the viewport-driven shrink to whatever size the call site
    // requested. On phone-class viewports the canvas backing store is
    // built at this reduced footprint; on desktop it's untouched.
    this.size = compactSize(opts.size ?? 72);
    this.min = opts.min;
    this.max = opts.max;
    this.defaultValue = clamp(opts.default, opts.min, opts.max);
    this.taper = opts.taper ?? "linear";
    this.style = opts.style ?? "arc";
    this.colorMode = opts.colorMode ?? "ice";
    this.ledCount = opts.ledCount ?? 17;
    this.step = opts.step ?? 0;
    this.format = opts.format ?? ((v) => v.toFixed(2));
    if (opts.onChange) this.onChange = opts.onChange;
    this.forced = opts.forceState;

    const initial = clamp(opts.value ?? this.defaultValue, opts.min, opts.max);
    this.value = initial;
    this.visualValue = initial;

    this.root = document.createElement("div");
    this.root.className = "knob";
    if (this.style === "led") this.root.classList.add("is-led");
    this.root.style.width = `${this.size}px`;
    this.root.style.height = `${this.size}px`;

    this.halo = document.createElement("div");
    this.halo.className = "knob-halo";

    this.canvas = document.createElement("canvas");
    // Canvas is larger than the visible knob so glow shadows can extend
    // past the knob's edges without being clipped. The .knob DOM box stays
    // sized to `this.size` for layout + pointer hit area.
    const totalPx = this.size + 2 * GLOW_PADDING;
    this.canvas.width = totalPx;
    this.canvas.height = totalPx;
    this.canvas.style.width = `${totalPx}px`;
    this.canvas.style.height = `${totalPx}px`;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("knob: 2d context unavailable");
    this.ctx = ctx;

    this.readout = document.createElement("output");
    this.readout.className = "knob-readout";
    this.readout.textContent = this.format(this.value);

    this.root.append(this.halo, this.canvas, this.readout);

    const wrapper = document.createElement("div");
    wrapper.className = "knob-cell";
    wrapper.append(this.root);
    if (opts.label) {
      this.labelEl = document.createElement("span");
      this.labelEl.className = "label knob-label";
      this.labelEl.textContent = opts.label;
      wrapper.append(this.labelEl);
    }
    opts.container.append(wrapper);

    this.applyForcedState();
    // Static showcase knobs need their glow at the target value on the
    // first paint — without this they'd lerp up from 0 over 200 ms and
    // the screenshot would catch them mid-fade.
    this.glowAmount = this.glowTarget();

    if (!this.forced) this.attachInteraction();

    this.draw();
    this.scheduleAnimation();
  }

  /** Target glow level for the current state. Dragging beats hovering. */
  private glowTarget(): number {
    if (this.forced === "disabled") return 0;
    if (this.dragging) return 0.75;
    if (this.hovering) return 0.55;
    return 0;
  }

  get current(): number {
    return this.value;
  }

  setValue(value: number): void {
    let next = clamp(value, this.min, this.max);
    if (this.step > 0) {
      // Snap to the nearest multiple of step. Clamp again after snapping
      // in case the rounded value lands outside [min, max] (e.g. min=-3,
      // step=1: -3.6 would round to -4 without re-clamping).
      next = clamp(
        Math.round(next / this.step) * this.step,
        this.min,
        this.max,
      );
    }
    if (next === this.value) return;
    this.value = next;
    this.readout.textContent = this.format(next);
    this.onChange?.(next);
    this.scheduleAnimation();
  }

  /** Reset to the construction-time default. */
  reset(): void {
    this.setValue(this.defaultValue);
  }

  /** Update the signal level driving the indicator's colour. Only has a
   * visible effect when `colorMode === "volume"`. Triggers a redraw. */
  setSignalLevel(level: number): void {
    const next = clamp(level, 0, 1);
    if (next === this.signalLevel) return;
    this.signalLevel = next;
    this.draw();
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    this.root.remove();
    if (this.labelEl) this.labelEl.remove();
  }

  // ----- interaction -----

  private attachInteraction(): void {
    // Pointer events live on the .knob root rather than the canvas. The
    // canvas is sized larger than the visible knob (for glow overflow); if
    // events were on the canvas, the hit area would include the empty
    // padding region. .knob's box matches the visual knob, so it's the
    // right hit target.
    this.root.addEventListener("pointerenter", () => {
      this.hovering = true;
      this.scheduleAnimation();
    });
    this.root.addEventListener("pointerleave", () => {
      this.hovering = false;
      this.scheduleAnimation();
    });

    this.root.addEventListener("pointerdown", (ev) => {
      ev.preventDefault();
      this.root.setPointerCapture(ev.pointerId);
      this.dragging = true;
      this.dragStartY = ev.clientY;
      this.dragStartValue = this.value;
      this.showReadout();
      this.scheduleAnimation();
    });

    this.root.addEventListener("pointermove", (ev) => {
      if (!this.dragging) return;
      const sensitivity = (ev.shiftKey ? 0.1 : 1) / FULL_RANGE_PX;
      // Up = increase, down = decrease.
      const deltaPx = this.dragStartY - ev.clientY;
      const normalized = deltaPx * sensitivity;
      this.applyNormalizedDelta(normalized);
    });

    const endDrag = (ev: PointerEvent): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.root.releasePointerCapture(ev.pointerId);
      this.scheduleReadoutFade();
      this.scheduleAnimation();
    };
    this.root.addEventListener("pointerup", endDrag);
    this.root.addEventListener("pointercancel", endDrag);

    this.root.addEventListener("dblclick", () => {
      this.reset();
    });
  }

  /** Apply a normalised drag delta (in 0..1 space) relative to drag start. */
  private applyNormalizedDelta(delta: number): void {
    const startNorm = this.toNormalised(this.dragStartValue);
    const targetNorm = clamp(startNorm + delta, 0, 1);
    this.setValue(this.fromNormalised(targetNorm));
  }

  /** Value → 0..1 normalized position. */
  private toNormalised(v: number): number {
    if (this.taper === "log") {
      const lo = Math.log(Math.max(this.min, 1e-6));
      const hi = Math.log(this.max);
      return (Math.log(Math.max(v, 1e-6)) - lo) / (hi - lo);
    }
    return (v - this.min) / (this.max - this.min);
  }

  /** 0..1 → value. */
  private fromNormalised(n: number): number {
    if (this.taper === "log") {
      const lo = Math.log(Math.max(this.min, 1e-6));
      const hi = Math.log(this.max);
      return Math.exp(lo + n * (hi - lo));
    }
    return this.min + n * (this.max - this.min);
  }

  private applyForcedState(): void {
    if (!this.forced) return;
    this.root.classList.add(`is-forced-${this.forced}`);
    if (this.forced === "hover" || this.forced === "active") {
      this.hovering = true;
    }
    if (this.forced === "active") {
      this.dragging = true;
      this.showReadout();
    }
    if (this.forced === "disabled") {
      this.root.classList.add("is-disabled");
    }
  }

  private showReadout(): void {
    clearTimeout(this.readoutFadeTimer);
    this.readout.classList.add("is-visible");
  }

  private scheduleReadoutFade(): void {
    clearTimeout(this.readoutFadeTimer);
    if (this.forced === "active") return;
    this.readoutFadeTimer = window.setTimeout(() => {
      this.readout.classList.remove("is-visible");
    }, 600);
  }

  // ----- rendering -----

  private scheduleAnimation(): void {
    if (this.rafHandle) return;
    this.lastRenderTime = performance.now();
    const step = (now: number): void => {
      const dt = now - this.lastRenderTime;
      this.lastRenderTime = now;

      // Two independent damped lerps share the frame loop: value chases
      // the user's target with a short tau (tactile), glow swells/fades
      // toward its hover/drag target with a slower tau (atmospheric).
      const vCoef = VISUAL_TAU_MS > 0 ? 1 - Math.exp(-dt / VISUAL_TAU_MS) : 1;
      this.visualValue += (this.value - this.visualValue) * vCoef;
      const gCoef = GLOW_TAU_MS > 0 ? 1 - Math.exp(-dt / GLOW_TAU_MS) : 1;
      const gTarget = this.glowTarget();
      this.glowAmount += (gTarget - this.glowAmount) * gCoef;

      this.draw();

      const valueSettled = Math.abs(this.visualValue - this.value) < 1e-4;
      const glowSettled = Math.abs(this.glowAmount - gTarget) < 1e-3;
      if (valueSettled && glowSettled) {
        this.visualValue = this.value;
        this.glowAmount = gTarget;
        this.draw();
        this.rafHandle = 0;
      } else {
        this.rafHandle = requestAnimationFrame(step);
      }
    };
    this.rafHandle = requestAnimationFrame(step);
  }

  private draw(): void {
    const dpr = window.devicePixelRatio || 1;
    const totalCss = this.size + 2 * GLOW_PADDING;
    const px = totalCss * dpr;
    if (this.canvas.width !== px) {
      this.canvas.width = px;
      this.canvas.height = px;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, totalCss, totalCss);
    // Shift the drawing origin so the visible knob centre aligns with the
    // centre of the .knob DOM box. All downstream drawing code can keep
    // using `this.size` as the knob's logical size.
    ctx.translate(GLOW_PADDING, GLOW_PADDING);

    const isDisabled = this.forced === "disabled";
    const valueNorm = clamp(this.toNormalised(this.visualValue), 0, 1);

    if (this.style === "led") {
      this.drawLedStyle(ctx, valueNorm, isDisabled);
    } else {
      this.drawArcStyle(ctx, valueNorm, isDisabled);
    }
  }

  /** Original style: large cap, recessed track arc, ice-blue active arc,
   *  radial indicator notch. */
  private drawArcStyle(
    ctx: CanvasRenderingContext2D,
    valueNorm: number,
    isDisabled: boolean,
  ): void {
    const cx = this.size / 2;
    const cy = this.size / 2;
    const r = this.size / 2;

    // Pass 1: dial cap. A subtle linear-ish gradient suggests the face
    // catching the same top-left light as the surrounding panel.
    const capRadius = r * 0.78;
    const capGrad = ctx.createLinearGradient(
      cx - capRadius * 0.7,
      cy - capRadius * 0.7,
      cx + capRadius * 0.7,
      cy + capRadius * 0.7,
    );
    capGrad.addColorStop(0, "#f3f6fa");
    capGrad.addColorStop(0.55, "#e9edf2");
    capGrad.addColorStop(1, "#dde2eb");
    ctx.fillStyle = capGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, capRadius, 0, TAU);
    ctx.fill();

    // Pass 2: brushed-material hint — a faint hairline rim catching light
    // along the top edge. Visible only at grazing angles in real life; here
    // we render it as a thin upper highlight.
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, capRadius - 0.5, 0, TAU);
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
    ctx.shadowColor = "rgba(255, 255, 255, 0.5)";
    ctx.shadowBlur = 1;
    ctx.stroke();
    ctx.restore();

    // Pass 3: recessed track — drawn as a thicker dark arc with a thinner
    // light highlight just inside it. Gives the appearance of a groove
    // carved into the dial cap.
    const trackR = capRadius * 0.9;
    const trackW = Math.max(2.5, this.size * 0.06);
    ctx.lineCap = "round";
    ctx.lineWidth = trackW;
    ctx.strokeStyle = "rgba(168, 181, 195, 0.45)";
    ctx.beginPath();
    ctx.arc(cx, cy, trackR, START_ANGLE, START_ANGLE + ARC_SWEEP);
    ctx.stroke();

    // Pass 4: active arc. Single glow channel drives shadow intensity and
    // colour brightness. At rest (g=0) the arc still has a faint trace of
    // glow so it reads as "luminous material at idle"; on interaction it
    // swells strongly toward peak.
    //
    // In volume mode the whole arc and notch share a colour sampled from
    // the green → amber → red ramp at the *signal level* — the knob
    // position determines how much arc is drawn, the signal driving the
    // control determines its colour.
    const valueAngle = START_ANGLE + ARC_SWEEP * valueNorm;
    const g = this.glowAmount;
    const indicatorRgb =
      this.colorMode === "volume"
        ? volumeColor(this.signalLevel)
        : lerpIce(g);
    const shadowRgb = this.colorMode === "volume" ? indicatorRgb : SHADOW_ICE;
    if (valueNorm > 0) {
      ctx.save();
      ctx.shadowColor = isDisabled
        ? "rgba(0, 0, 0, 0)"
        : rgbaStr(shadowRgb, 0.3 + g * 0.65);
      ctx.shadowBlur = isDisabled ? 0 : 2 + g * 18;
      ctx.lineWidth = trackW;
      ctx.strokeStyle = isDisabled
        ? "rgba(124, 135, 148, 0.7)"
        : rgbStr(indicatorRgb);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(cx, cy, trackR, START_ANGLE, valueAngle);
      ctx.stroke();
      ctx.restore();
    }

    // Pass 5: indicator notch — same glow channel, smaller scale.
    const notchInner = capRadius * 0.45;
    const notchOuter = capRadius * 0.84;
    const nx0 = cx + Math.cos(valueAngle) * notchInner;
    const ny0 = cy + Math.sin(valueAngle) * notchInner;
    const nx1 = cx + Math.cos(valueAngle) * notchOuter;
    const ny1 = cy + Math.sin(valueAngle) * notchOuter;

    ctx.save();
    ctx.shadowColor = isDisabled
      ? "rgba(0, 0, 0, 0)"
      : rgbaStr(shadowRgb, 0.4 + g * 0.55);
    ctx.shadowBlur = isDisabled ? 0 : 1 + g * 14;
    ctx.lineCap = "round";
    ctx.lineWidth = Math.max(2, this.size * 0.045);
    ctx.strokeStyle = isDisabled
      ? "rgba(124, 135, 148, 0.85)"
      : rgbStr(indicatorRgb);
    ctx.beginPath();
    ctx.moveTo(nx0, ny0);
    ctx.lineTo(nx1, ny1);
    ctx.stroke();
    ctx.restore();
  }

  /** LED-ring style: smaller raised cap, a ring of discrete dots showing
   *  value as quantised steps, plus a single bright face dot for fine
   *  position. The .knob wrapper drops its CSS box-shadow when this style
   *  is active so the cap can wear its own canvas-rendered shadow. */
  private drawLedStyle(
    ctx: CanvasRenderingContext2D,
    valueNorm: number,
    isDisabled: boolean,
  ): void {
    const cx = this.size / 2;
    const cy = this.size / 2;
    const r = this.size / 2;

    // Geometry — leave room for both LED ring and the cap's own shadow.
    const capRadius = r * 0.62;
    const ringR = r * 0.92;
    const ledR = Math.max(1.1, this.size * 0.025);

    // Pass 1: the LED ring (drawn first so the cap's shadow can sit over
    // any halo from the lit LEDs near the edge).
    //
    // Volume mode: lit LEDs all share the colour sampled at the current
    // signal level. The number of lit LEDs is still determined by the
    // knob's value (gain setting); the colour is determined by what's
    // flowing through.
    const count = this.ledCount;
    const g = this.glowAmount;
    const volRgb =
      this.colorMode === "volume" ? volumeColor(this.signalLevel) : null;
    for (let i = 0; i < count; i++) {
      const norm = i / (count - 1);
      const angle = START_ANGLE + ARC_SWEEP * norm;
      const x = cx + Math.cos(angle) * ringR;
      const y = cy + Math.sin(angle) * ringR;
      const lit = !isDisabled && valueNorm > 0 && norm <= valueNorm + 1e-3;

      if (lit) {
        const ledRgb = volRgb ?? lerpIce(g);
        ctx.save();
        ctx.shadowColor = rgbaStr(ledRgb, 0.45 + g * 0.55);
        ctx.shadowBlur = 2 + g * 12;
        ctx.fillStyle = rgbStr(ledRgb);
        ctx.beginPath();
        ctx.arc(x, y, ledR, 0, TAU);
        ctx.fill();
        ctx.restore();
      } else {
        // Unlit dot — small, recessed-looking, no glow.
        ctx.fillStyle = isDisabled
          ? "rgba(124, 135, 148, 0.35)"
          : "rgba(124, 135, 148, 0.4)";
        ctx.beginPath();
        ctx.arc(x, y, ledR * 0.7, 0, TAU);
        ctx.fill();
      }
    }

    // Pass 2: cap shadow + body. Two-pass shadow trick produces the same
    // dual-direction "raised" lighting that the CSS box-shadow gives the
    // arc-style knob — but here it lives inside the canvas so the .knob
    // wrapper itself stays transparent and doesn't push the LED ring onto
    // a shadowed background.
    //
    // The first fill drops the dark bottom-right shadow; the second draws
    // the same cap with the light top-left highlight. Both fills produce
    // the same cap shape on top, so visually you see one cap with both
    // shadows around it.
    ctx.save();
    ctx.shadowColor = "rgba(168, 181, 195, 0.55)";
    ctx.shadowOffsetX = 3;
    ctx.shadowOffsetY = 3;
    ctx.shadowBlur = 8;
    const capGradA = ctx.createLinearGradient(
      cx - capRadius * 0.7,
      cy - capRadius * 0.7,
      cx + capRadius * 0.7,
      cy + capRadius * 0.7,
    );
    capGradA.addColorStop(0, "#f3f6fa");
    capGradA.addColorStop(0.55, "#e9edf2");
    capGradA.addColorStop(1, "#dde2eb");
    ctx.fillStyle = capGradA;
    ctx.beginPath();
    ctx.arc(cx, cy, capRadius, 0, TAU);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.shadowColor = "rgba(255, 255, 255, 0.85)";
    ctx.shadowOffsetX = -3;
    ctx.shadowOffsetY = -3;
    ctx.shadowBlur = 8;
    ctx.fillStyle = capGradA;
    ctx.beginPath();
    ctx.arc(cx, cy, capRadius, 0, TAU);
    ctx.fill();
    ctx.restore();

    // Pass 3: hairline rim catching light at grazing angle.
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, capRadius - 0.5, 0, TAU);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
    ctx.stroke();
    ctx.restore();

    // Pass 4: face indicator dot — bright ice-blue, tracks the value
    // angle continuously (smoother than the LED ring's discrete steps).
    const valueAngle = START_ANGLE + ARC_SWEEP * valueNorm;
    const dotDist = capRadius * 0.7;
    const dotX = cx + Math.cos(valueAngle) * dotDist;
    const dotY = cy + Math.sin(valueAngle) * dotDist;
    const dotR = Math.max(1.8, this.size * 0.045);

    if (isDisabled) {
      ctx.fillStyle = "rgba(124, 135, 148, 0.7)";
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotR, 0, TAU);
      ctx.fill();
    } else {
      // Face dot: glow is fully gated by interaction. At g=0 the dot is
      // a plain filled circle with no shadow at all; on hover/drag a
      // bright halo fades in. In volume mode the dot's colour matches the
      // ramp at the current signal level (so it goes red when the audio
      // through the knob is clipping, regardless of the gain setting);
      // in ice mode it brightens toward white-blue with glow.
      const dotRgb = volRgb ?? lerpIce(g);
      ctx.save();
      if (g > 0.001) {
        ctx.shadowColor = rgbaStr(dotRgb, g);
        ctx.shadowBlur = g * 18;
      }
      ctx.fillStyle = rgbStr(dotRgb);
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotR, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
